import React, { type FC, useMemo, useCallback, useEffect } from 'react';
import { Formik, Form, type FormikHelpers } from 'formik';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import {
  UiButton,
  UiHStack, UiIconCaretLeft, UiIconCaretRight,
  UiStack
  ,
} from '@/lib/ui';
import FormGenerator from '@/base/FormGenerator';
import { createUtils, type FieldsValues } from '@/base/FormGenerator/utils';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import { useTenantApi } from '@/account/hook/useTenantApi';
import {
  type InfoFormByAttendeeCategoryQuery,
  InfoFormType,
  type InfoForm,
  type InfoFormResponse,
  loadRegistrationInfoFormResponse,
  infoFormByAttendeeCategoryQueryKey,
  loadInfoFormByAttendeeCategory,
  infoFormResponseQueryKey,
  type InfoFormResponseSaveRequest,
  saveInfoFormResponse,
  registrationQueryKey,
  FormType,
} from '@/api/registration';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { type FieldMetadataInput } from '@/base/FormBuilder/Field';
import InfoBox from '@/registration/component/InfoBox';
import { type CountryCode } from 'libphonenumber-js';
import { validatePhoneNumber } from '@/lib/util/helper';
import BrandFooter from '@/registration/component/Register/Layout/BrandFooter';
import BaseThemeButton from '@/base/ThemeButton';

type FormData = FieldsValues;
type FieldName = keyof FieldsValues;

type Errors = Record<FieldName, string>;

export interface PersonalInfoFormProps {
}

const PersonalInfoForm: FC<PersonalInfoFormProps> = () => {
  const { eventId } = useRegisterRoute();
  const queryClient = useQueryClient();
  const registrationQuery = useRegistrationQuery(eventId);
  const { toNextStep, toPreviousStep, isReviewing } = useRegisterSteps();
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { data: eventSetting } = useEventSettingsQuery(eventId);

  const infoFormByAttendeeCategoryQuery = useQuery<InfoForm, Error>(
    [infoFormByAttendeeCategoryQueryKey, { registrationId: registrationQuery?.data?.id, formType: InfoFormType.PERSONAL }],
    async () => {
      if (!registrationQuery?.data?.id) {
        throw new Error('PersonalInfoQuery: registrationId is undefined');
      }
      const params: InfoFormByAttendeeCategoryQuery = { formType: InfoFormType.PERSONAL, registrationId: registrationQuery?.data?.id };
      const result = await loadInfoFormByAttendeeCategory(createTenantApiRequest)(params);
      if (result.item === undefined) {
        throw new Error(result.errors?.join('\n') ?? 'Something went wrong');
      }
      return result.item;
    },
    {
      enabled: !!registrationQuery?.data?.id && !isApiPreparing,
      cacheTime: 0 // Disable caching
    }
  );

  const personalInfoQuery = useQuery<InfoFormResponse, Error>({
    queryKey: [infoFormResponseQueryKey, { registrationId: registrationQuery?.data?.id, type: InfoFormType.PERSONAL }],
    queryFn: async () => {
      if (!registrationQuery?.data?.id) {
        throw new Error('PersonalInfoQuery: registrationId is undefined');
      }
      const response = await loadRegistrationInfoFormResponse(createTenantApiRequest)({
        registrationId: registrationQuery?.data?.id,
        type: InfoFormType.PERSONAL
      });
      return response?.item ?? {};
    },
    enabled: !!registrationQuery?.data?.id && !isApiPreparing
  });

  const utils = useMemo(
    () => { return createUtils((infoFormByAttendeeCategoryQuery.data?.config?.fieldsMetadata ?? [])); },
    [infoFormByAttendeeCategoryQuery.data?.config?.fieldsMetadata]);

  const { mutate, isLoading } = useMutation<{}, Error, InfoFormResponseSaveRequest>(
    {
      mutationFn: async (data: InfoFormResponseSaveRequest) => { return await saveInfoFormResponse(createTenantApiRequest)(data); },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [infoFormByAttendeeCategoryQueryKey] });
        await queryClient.invalidateQueries([infoFormResponseQueryKey, { registrationId: registrationQuery?.data?.id, type: InfoFormType.PERSONAL }]);
        await queryClient.invalidateQueries([registrationQueryKey]);
        toNextStep();
      }
    }
  );

  const submitForm = useCallback(async (values: FormData) => {
    if (!registrationQuery?.data?.id) {
      throw new Error("Can't submit Personal Info Form, Registration ID is not found");
    }
    mutate({
      eventId,
      formType: 'personal' as FormType,
      response: values,
      ownerId: registrationQuery?.data?.id,
      ownerType: 'Registration',
      infoFormId: infoFormByAttendeeCategoryQuery?.data?.id
    });
  }, [eventId, infoFormByAttendeeCategoryQuery?.data?.id, mutate, registrationQuery?.data?.id]);

  useEffect(() => {
    if (registrationQuery?.data?.registrationStrategy === 'payment_first') {
      void registrationQuery.refetch();
    }
  }, [queryClient, registrationQuery?.data?.registrationStrategy]);

  return (
    <QueryContainer query={infoFormByAttendeeCategoryQuery}>
      {(infoForm) => {
        return (
          <QueryContainer query={personalInfoQuery}>
            {(personalInfoForm) => {
              return (
                <Formik
                  initialValues={utils.getFieldsValues(personalInfoForm.response)}
                  validateOnChange={false}
                  validateOnBlur={false}
                  validate={(values: FormData) => {
                    const errors: Errors = {};

                    // This is an example to check 'required' fields. We might validator others in the future.
                    for (const fieldId in values) {
                      const fieldMetadata = utils.getFieldMetadata(fieldId);
                      if (fieldMetadata) {
                        // check for phone type
                        if ('isRequired' in fieldMetadata && fieldMetadata.isRequired && fieldMetadata.type === 'phone') {
                          const phoneValue = (values[fieldId] as string ?? '').replaceAll(' ', '');
                          const currentDialCode = values[`${fieldId}-countryCode`] as string;
                          const isValid = validatePhoneNumber({ phone: phoneValue, country: currentDialCode as CountryCode });
                          if (!isValid) {
                            errors[fieldId] = 'Invalid phone number';
                          }
                        }
                        // If a field contains exmpty string, we will treat it as empty. Currently we don't consider number or boolean.
                        if ('isRequired' in fieldMetadata && fieldMetadata.isRequired && values[fieldId] === '') {
                          errors[fieldId] = 'The field is required';
                        }
                        if ((fieldMetadata as FieldMetadataInput).length && values[fieldId]) {
                          if ((values[fieldId] as string)?.length > (fieldMetadata as FieldMetadataInput).length!) {
                            errors[fieldId] = `The field can not have more than ${(fieldMetadata as FieldMetadataInput).length} characters.`;
                          }
                        }
                      }
                    }
                    return errors;
                  }}
                  onSubmit={async (
                    values,
                    { setSubmitting }: FormikHelpers<FormData>
                  ) => {
                    const formKeys = Object.keys(utils.getFieldsValues(personalInfoForm.response));
                    const formValues = formKeys.reduce((acc: Record<string, any>, key) => {
                      acc[key] = values[key];
                      return acc;
                    }, {});
                    setSubmitting(true);
                    await submitForm(formValues);
                    setSubmitting(false);
                  }}
                >
                  <Form>
                    <BaseTransitionSlideLeft>
                      <UiStack flexGrow={1} spacing={8}>
                        {(eventSetting?.pageMetadata?.personalInfo?.title || eventSetting?.pageMetadata?.personalInfo?.description) && (
                          <InfoBox
                            title={eventSetting?.pageMetadata?.personalInfo?.title}
                            description={eventSetting?.pageMetadata?.personalInfo?.description}
                          />
                        )}
                        <FormGenerator
                          fieldsLayout={infoForm.config.fieldsLayout}
                          fieldsMetadata={infoForm.config.fieldsMetadata}
                        />
                      </UiStack>
                    </BaseTransitionSlideLeft>
                    <UiStack height={96} />
                    <LayoutFooter>
                      <UiHStack justifyContent={'space-between'} flexGrow={1}>
                        {/*<UiButton*/}
                        {/*  size={'lg'}*/}
                        {/*  colorScheme={'primary'}*/}
                        {/*  onClick={toPreviousStep}*/}
                        {/*  variant={'ghost'}*/}
                        {/*  fontSize={'lg'}*/}
                        {/*  leftIcon={(*/}
                        {/*    <UiIconCaretLeft color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
                        {/*  )}*/}
                        {/*>*/}
                        {/*  {isReviewing ? 'Back' : 'Prev'}*/}
                        {/*</UiButton>*/}
                        <BaseThemeButton
                          theme={'gray'}
                          onClick={toPreviousStep}
                          leftIcon={(
                            <UiIconCaretLeft color={'primary.800'} size={'lg'} weight={'bold'}/>
                          )}
                        >
                          {isReviewing ? 'Back' : 'Prev'}
                        </BaseThemeButton>
                        <BrandFooter/>
                        <BaseThemeButton
                          theme={'primary'}
                          isLoading={isLoading}
                          type={'submit'}
                          rightIcon={(
                            <UiIconCaretRight color={'primary.800'} size={'lg'} weight={'bold'}/>
                          )}
                        >
                          {isReviewing ? 'Done' : 'Next'}
                        </BaseThemeButton>
                      </UiHStack>
                    </LayoutFooter>
                  </Form>
                </Formik>
              );
            }}
          </QueryContainer>
        );
      }}
    </QueryContainer>
  );
};

export default React.memo(PersonalInfoForm);
